import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type Organisation,
  type UpdateOrganisationRequest,
  type UploadOrganisationLogoResponse,
} from '../client';

/**
 * Stable query-key factory for organisation-scoped caches. Apps invalidate
 * via these constants rather than hand-rolled tuples so cache surgery stays
 * consistent across screens.
 */
export const organisationKeys = {
  all: ['directory', 'organisation'] as const,
  byId: (id: string) => ['directory', 'organisation', id] as const,
};

/**
 * Fetch a single organisation by id. Pass `null` while the id is unknown
 * (e.g. before the Auth0 token resolves) — the query stays disabled.
 */
export function useOrganisation(
  id: string | null,
  options?: Omit<UseQueryOptions<Organisation, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<Organisation, Error>({
    queryKey: id ? organisationKeys.byId(id) : organisationKeys.all,
    queryFn: () => {
      if (!id) throw new Error('No organisation id');
      return directoryApi.getOrganisation(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    ...options,
  });
}

/**
 * Mutation wrapper around PUT `/organisations`. On success invalidates the
 * single-org cache so any open page refetches the canonical server copy.
 */
export function useUpdateOrganisation() {
  const qc = useQueryClient();
  return useMutation<string, Error, UpdateOrganisationRequest>({
    mutationKey: [...organisationKeys.all, 'update'],
    mutationFn: (body) => directoryApi.updateOrganisation(body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: organisationKeys.byId(vars.organisationId) });
    },
  });
}

/**
 * Atomic update for the org's additional-currency list. The mutation fires
 * the dedicated endpoint (rather than echoing the full org body), so it
 * doesn't compete with whatever else might be in flight on the page.
 * Variables: `{ organisationId, codes }`.
 */
export function useUpdateOrganisationAdditionalCurrencies() {
  const qc = useQueryClient();
  return useMutation<string, Error, { organisationId: string; codes: string[] }>({
    mutationKey: [...organisationKeys.all, 'update-additional-currencies'],
    mutationFn: ({ organisationId, codes }) =>
      directoryApi.updateOrganisationAdditionalCurrencies(organisationId, { codes }),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: organisationKeys.byId(organisationId) });
    },
  });
}

/**
 * Atomic update for the organisation's TMS order-reference-label map.
 * Drives the per-org slot names the TMS Order Details page shows next
 * to `Order.Reference1..4` (and any long-tail slots beyond 4). Pass
 * `labelsJson: null` to clear the column — the BE accepts `null` and
 * rejects malformed / non-object JSON with 422.
 *
 * <p>
 * Variables: `{ organisationId, labelsJson }`. Cache invalidates the
 * by-id query so the page picks up the new shape on the next read.
 * </p>
 */
export function useUpdateOrganisationTmsOrderReferenceLabels() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { organisationId: string; labelsJson: string | null }
  >({
    mutationKey: [...organisationKeys.all, 'update-tms-order-reference-labels'],
    mutationFn: ({ organisationId, labelsJson }) =>
      directoryApi.updateOrganisationTmsOrderReferenceLabels(organisationId, { labelsJson }),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: organisationKeys.byId(organisationId) });
    },
  });
}

/**
 * Upload (or replace) an organisation's logo. The mutation:
 *
 *  1. Streams the file via multipart POST.
 *  2. On success, **eagerly patches** the cached `Organisation` so the
 *     <img src=…> rerenders immediately with the freshly returned SAS URL —
 *     no flash-of-old-logo while the invalidate refetch is in flight.
 *  3. Then invalidates the by-id query so the next read still goes through
 *     the canonical server response (which will have a freshly minted SAS).
 *
 * Variables: `{ id, file }`. Result: the `UploadOrganisationLogoResponse`
 * carrying the new blob name + SAS URL.
 */
export function useUploadOrganisationLogo() {
  const qc = useQueryClient();
  return useMutation<UploadOrganisationLogoResponse, Error, { id: string; file: File }>({
    mutationKey: [...organisationKeys.all, 'upload-logo'],
    mutationFn: ({ id, file }) => directoryApi.uploadOrganisationLogo(id, file),
    onSuccess: (res, { id }) => {
      qc.setQueryData<Organisation>(organisationKeys.byId(id), (prev) =>
        prev ? { ...prev, logo: res.logo, logoUrl: res.logoUrl } : prev,
      );
      qc.invalidateQueries({ queryKey: organisationKeys.byId(id) });
    },
  });
}

/**
 * Clear an organisation's logo. Optimistically patches the cache to drop the
 * URL so the UI flips back to placeholder mode without waiting for the
 * server round-trip; on failure the invalidate fixes the cache up.
 */
export function useDeleteOrganisationLogo() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string }>({
    mutationKey: [...organisationKeys.all, 'delete-logo'],
    mutationFn: ({ id }) => directoryApi.deleteOrganisationLogo(id),
    onSuccess: (_res, { id }) => {
      qc.setQueryData<Organisation>(organisationKeys.byId(id), (prev) =>
        prev ? { ...prev, logo: null, logoUrl: null } : prev,
      );
      qc.invalidateQueries({ queryKey: organisationKeys.byId(id) });
    },
  });
}
